import {
  Card,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import {
  ProgressButton,
  ProgressButtonProps,
  DialogRoot
} from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";

import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { ProductCard } from "~/components/productCard/ProductCardRootWithSideRibbon";
import { AutostoreBin } from "~/features/autostoreBin";

import { getHoldTypeOptions } from "~/lib/helpers";
import {
  mixpanelSetHoldConfirm,
  mixpanelTrack,
  useMixpanelPageName,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";

import { flagBin } from "~/redux/actions";
import { InventoryHoldReasonCode } from "~/redux/public/inventory.openApi";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectPickingStateArgs } from "~/redux/selectors/autostoreSelectors";
import {
  selectIsShortSideBin,
  selectSelectedPortId,
  selectWorkstationAutostoreGridId,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import { useGetBinQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import {
  useGetFocusedPickQuery,
  useGetBatchPickingStateQuery
} from "~/redux/warehouse/autostorePicking.hooks";

import { useHoldBinMutation } from "~/redux/warehouse/bin.hooks";

import {
  useGetInventoryWithVariantInfoByAutostoreBinNumberQuery,
  useLazyGetInventoryByIdQuery
} from "~/redux/warehouse/inventory.hooks";

import { useRefetchBatchPickingState } from "./hooks/useRefetchBatchPickingState";

import {
  closeHoldBinModal,
  openHoldBinModal,
  selectCompartment,
  selectProblemType
} from "./problemSolveModal.slice";

type HoldTypeText =
  | InventoryHoldReasonCode
  | "damaged product"
  | "expired product";

const ProblemSolveModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const trackedModalName = useMixpanelModalName("Bin and Inventory Problem");

  const selectedCompartment =
    useAppSelector((state) => state.problemSolveModal.selectedCompartment) ?? 1;
  const selectedProblemCode = useAppSelector(
    (state) => state.problemSolveModal.problemType
  );
  const isFullBinProblem =
    selectedProblemCode?.toLowerCase().includes("bin") ?? false;
  const workstationId = useAppSelector(selectWorkstationId);
  const gridId = useAppSelector(selectWorkstationAutostoreGridId);
  const portId = useAppSelector(selectSelectedPortId);
  const {
    ap_excludeRecalledHold,
    ap_includeDamagedBinHold,
    ap_includeDirtyBinHold,
    ap_includeMisconfiguredBinHold
  } = useAppSelector(selectServerConfig);
  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const pickingStateArgs = useAppSelector(selectPickingStateArgs);
  useGetBatchPickingStateQuery(pickingStateArgs);
  const refetchPickingState = useRefetchBatchPickingState();
  const { focusedPickBinNumber, isPickLoading } = useGetFocusedPickQuery(
    workstationId
      ? {
          workstationId: workstationId
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, isLoading }) => ({
        focusedPickBinNumber: data?.autostoreBinId,
        isPickLoading: isLoading
      })
    }
  );

  const { data: inventoryInBin, isLoading: isInventoryInBinLoading } =
    useGetInventoryWithVariantInfoByAutostoreBinNumberQuery(
      focusedPickBinNumber && gridId
        ? {
            autostoreGridId: gridId,
            binNumber: focusedPickBinNumber
          }
        : skipToken,
      { refetchOnMountOrArgChange: true }
    );

  const isInventoryOnHold = inventoryInBin?.some(
    (inv) =>
      inv.inventory.holds.length &&
      inv.inventory.bin.autostoreCompartmentNumber === selectedCompartment
  );
  const isBinFlagged = inventoryInBin?.some(
    (inv) => inv.inventory.bin.autostoreBin?.isFlagged
  );

  const { data: bin, isLoading: isBinLoading } = useGetBinQuery(
    focusedPickBinNumber && gridId
      ? { autostoreGridId: gridId, binNumber: focusedPickBinNumber }
      : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const binConfiguration = bin?.binConfiguration;

  const [holdBin] = useHoldBinMutation();
  const [getInventoryByIdQuery] = useLazyGetInventoryByIdQuery();

  const holdTypeOptions = getHoldTypeOptions({
    excludeRecalledHold: ap_excludeRecalledHold,
    includeDamagedHold: ap_includeDamagedBinHold,
    includeDirtyBinFlag: ap_includeDirtyBinHold,
    includeMisconfiguredBinFlag: ap_includeMisconfiguredBinHold,
    includeOutOfStockHold: false,
    includeExpiredHold: true
  });

  const isLoading = isPickLoading || isBinLoading || isInventoryInBinLoading;

  const variantToDisplay = inventoryInBin
    ? inventoryInBin.find(
        (specificInventory) =>
          specificInventory.inventory.bin.autostoreCompartmentNumber ===
          selectedCompartment
      )
    : null;

  const confirmCb = async () => {
    const selectedBinId =
      (bin?.compartments ?? []).find(
        (compartment) =>
          compartment.autostoreCompartmentNumber === selectedCompartment
      )?.binId ?? null;

    if (
      selectedProblemCode &&
      focusedPickBinNumber &&
      selectedBinId &&
      gridId &&
      portId &&
      workstationId
    ) {
      mixpanelSetHoldConfirm(
        trackedModalName,
        "Button Click",
        "Set Hold",
        selectedProblemCode,
        isFullBinProblem ? "N/A" : selectedCompartment,
        focusedPickBinNumber
      );

      // We have two types of reason codes:
      // 1. Related to a specific inventory in a specific compartment
      // 2. Related to the entire bin
      // If the selected reason code is associated with the entire bin,
      // The flag Bin api should be called
      if (selectedProblemCode.length && selectedProblemCode.includes("bin")) {
        const reasonCodeExtracted = selectedProblemCode.split(" ")[0];
        // TODO move this to RTK
        await dispatch(
          flagBin(gridId, portId, focusedPickBinNumber, reasonCodeExtracted)
        );
      } else {
        await holdBin({
          warehouseBinId: selectedBinId,
          reasonCode: selectedProblemCode,
          gridId: gridId,
          portId: portId,
          workstationId: workstationId
        });
      }

      // When the hold is placed on inventory/bin,
      // useGetInventoryByIdQuery won't be refetched because the inventoryId argument is the same
      // We need to refetch query in order to get updated UI
      if (variantToDisplay)
        await getInventoryByIdQuery({
          inventoryId: variantToDisplay.inventory.inventoryId
        });

      await refetchPickingState();
      dispatch(closeHoldBinModal());
    }
  };

  return (
    <DialogRoot
      open={!isLoading}
      onClose={() => dispatch(closeHoldBinModal())}
      maxWidth={"md"}
      fullWidth
    >
      <Card>
        <DialogTitle sx={{ mb: 3 }}>
          {t("bin and inventory problems")}
        </DialogTitle>
        <DialogContent sx={{ overflowY: "hidden" }}>
          <Grid container direction="column">
            <Grid container item>
              <Grid container style={{ width: "100%" }}>
                {variantToDisplay ? (
                  <Grid item xs={6}>
                    <ProductCard.Root
                      productName={variantToDisplay.variant.productName}
                      imageFileName={variantToDisplay.variant.imageFilename}
                      tempZone={variantToDisplay.variant.temperatureZone}
                      style={{ height: "100%" }}
                    >
                      <ProductCard.Sku sku={variantToDisplay.variant.sku} />
                    </ProductCard.Root>
                  </Grid>
                ) : (
                  <Grid item xs={6}>
                    <ProductCard.Loading innerHeight="220px" />
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Grid
                    container
                    direction="column"
                    sx={{ position: "relative" }}
                  >
                    {!isFullBinProblem &&
                      (binConfiguration?.numberOfCompartments ?? 1) > 1 && (
                        <Grid item sx={{ marginBottom: "1em" }}>
                          <Typography variant="h6">
                            {t("select bin compartment")}:
                          </Typography>
                        </Grid>
                      )}

                    <Grid
                      item
                      sx={{
                        transform: "translateX(-5%)",
                        marginBottom: "1em",
                        width: "100%"
                      }}
                    >
                      <Stack
                        alignItems="center"
                        height={isShortSideBin ? "300px" : "unset"}
                      >
                        <AutostoreBin
                          state={"Bin Opened"}
                          numberOfColumns={
                            binConfiguration?.verticalCompartmentCount || 1
                          }
                          numberOfRows={
                            binConfiguration?.horizontalCompartmentCount || 1
                          }
                          pickCompartment={null}
                          pickQuantity=""
                          binId={focusedPickBinNumber}
                          hideBinId
                          selectedCompartment={
                            isFullBinProblem
                              ? undefined
                              : selectedCompartment - 1
                          }
                          onBinClick={(num) => {
                            if (typeof num === "number") {
                              mixpanelTrack({
                                trackedPageName: trackedModalName,
                                type: "Button Click",
                                label: "Select Bin Compartment",
                                eventProperties: {
                                  Compartment: num + 1,
                                  "Bin Number": focusedPickBinNumber ?? ""
                                }
                              });
                              dispatch(selectCompartment(num + 1));
                            }
                          }}
                          isSelectedCompartmentInventoryOnHold={
                            isInventoryOnHold
                          }
                          isBinFlagged={isBinFlagged}
                          isShortSideBin={isShortSideBin}
                        />
                        {isBinFlagged && (
                          <Typography variant="h6">
                            {t("bin is flagged", { binNumber: "" })}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>
                    <Grid
                      item
                      container
                      sx={{
                        position: "relative",
                        alignItems: "end"
                      }}
                    >
                      <Grid item xs={6} sx={{ height: "100%" }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600, mt: 0.5 }}
                          id="hold-bin-dropdown"
                        >
                          {t("choose a hold type")}
                        </Typography>
                        <TextField
                          value={selectedProblemCode ?? ""}
                          select
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-root": {
                              height: 65
                            },
                            width: "90%",
                            "& .MuiSelect-select .notranslate::after": {
                              content: `"${t("available hold types")}"`,
                              opacity: 0.52
                            }
                          }}
                          slotProps={{
                            htmlInput: {
                              sx: { textAlign: "center" },
                              "aria-describedby": "hold-bin-dropdown",
                              "aria-label": "Choose a hold type"
                            }
                          }}
                        >
                          {holdTypeOptions.map((option) => {
                            let menuItemText: HoldTypeText = option.reasonCode;
                            if (menuItemText === "damaged") {
                              menuItemText = "damaged product";
                            } else if (menuItemText === "expired") {
                              menuItemText = "expired product";
                            }
                            return (
                              <MenuItem
                                onClick={() => {
                                  mixpanelTrack({
                                    trackedPageName: trackedModalName,
                                    type: "Select Option",
                                    label: "Select Reason Type",
                                    eventProperties: {
                                      "Reason Type": option.reasonCode
                                    }
                                  });
                                  dispatch(
                                    selectProblemType(option.reasonCode)
                                  );
                                }}
                                key={option.reasonCode.toLocaleLowerCase()}
                                value={option.reasonCode.toLocaleLowerCase()}
                                style={{ padding: "1.5em 0 1.5em 0.5em" }}
                              >
                                <Typography>{t(menuItemText)}</Typography>
                              </MenuItem>
                            );
                          })}
                        </TextField>
                      </Grid>
                      <Grid item>
                        <ProgressButton
                          buttonSize="large"
                          emphasis="high"
                          responsive
                          variant="contained"
                          color="primary"
                          onClick={confirmCb}
                          fullWidth
                          disabled={!selectedProblemCode}
                          style={{ width: "9em" }}
                        >
                          {t("confirm")}
                        </ProgressButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Card>
    </DialogRoot>
  );
};

const ProblemSolveModalButton = (
  props: Omit<ProgressButtonProps, "onClick">
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.problemSolveModal.isOpen);
  const trackedPageName = useMixpanelPageName();

  return (
    <>
      <ProgressButton
        aria-label={"problem-solve-button"}
        onClick={() => {
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Hold Bin"
          });
          dispatch(openHoldBinModal());
        }}
        {...props}
      >
        {t("hold bin")}
      </ProgressButton>
      {isOpen && <ProblemSolveModal />}
    </>
  );
};

export default ProblemSolveModalButton;
